import { useState, useMemo } from 'react';
import CommonModal from '../common/Modal';
import { useFirestore, useFirestoreOrderBy } from '../../firebase/useFirestore';

// `outputs` and `date` event fields are not syncable as activity fields —
// outputs sync via the Outputs collection, date fields carry no data.
const NON_SYNCABLE_EVENT_FIELD_TYPES = ['outputs', 'date'];

/**
 * Portfolio→project: pick a portfolio's outputs and event fields to request as
 * synchronisations, grouped by the activity they belong to. The modal owns its
 * own selection; on confirm it hands the picked docs to `onConfirm`.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is shown.
 * @param {(open: boolean) => void} props.setModalOpen - Toggles the modal.
 * @param {(selection: { outputs: object[], eventFields: object[] }) => void} props.onConfirm
 *   Called with the picked portfolio Output and EventField docs.
 */
const AddActivitySyncsModal = ({ modalOpen, setModalOpen, onConfirm }) => {
    const activities = useFirestoreOrderBy('Activities', 'Position', 'asc');
    const outputsRaw = useFirestoreOrderBy('Outputs', 'Position', 'asc');
    const outputs = useMemo(() => (Array.isArray(outputsRaw) ? outputsRaw : []).filter((o) => !o.Deleted), [outputsRaw]);
    const eventForms = useFirestore('EventForms');
    const eventFields = useFirestore('EventFields');

    const [expandedActivityId, setExpandedActivityId] = useState('');
    const [selectedOutputIds, setSelectedOutputIds] = useState(new Set());
    const [selectedEventFieldIds, setSelectedEventFieldIds] = useState(new Set());

    const activityList = useMemo(
        () => (Array.isArray(activities) ? activities : []),
        [activities]
    );

    const outputsByActivity = useMemo(() => {
        const map = {};
        (Array.isArray(outputs) ? outputs : []).forEach((output) => {
            if (!output.ActivityID) return;
            (map[output.ActivityID] ||= []).push(output);
        });
        return map;
    }, [outputs]);

    // EventFields link to an activity via EventForms (EventField.EventId →
    // EventForm.ID → EventForm.ActivityID).
    const eventFieldsByActivity = useMemo(() => {
        const activityByFormId = {};
        (Array.isArray(eventForms) ? eventForms : []).forEach((form) => {
            if (form.ID) activityByFormId[form.ID] = form.ActivityID;
        });

        const map = {};
        (Array.isArray(eventFields) ? eventFields : []).forEach((field) => {
            if (NON_SYNCABLE_EVENT_FIELD_TYPES.includes(field.Type)) return;
            const activityId = activityByFormId[field.EventId];
            if (!activityId) return;
            (map[activityId] ||= []).push(field);
        });
        return map;
    }, [eventFields, eventForms]);

    const toggleId = (setFn) => (id) => {
        setFn((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleOutput = toggleId(setSelectedOutputIds);
    const toggleEventField = toggleId(setSelectedEventFieldIds);

    const resetState = () => {
        setExpandedActivityId('');
        setSelectedOutputIds(new Set());
        setSelectedEventFieldIds(new Set());
    };

    const closeModal = () => {
        resetState();
        setModalOpen(false);
    };

    const handleConfirm = () => {
        const pickedOutputs = (Array.isArray(outputs) ? outputs : []).filter(
            (output) => selectedOutputIds.has(output.ID)
        );
        const pickedEventFields = (Array.isArray(eventFields) ? eventFields : []).filter(
            (field) => selectedEventFieldIds.has(field.ID)
        );
        onConfirm({ outputs: pickedOutputs, eventFields: pickedEventFields });
        closeModal();
    };

    const totalSelected = selectedOutputIds.size + selectedEventFieldIds.size;

    const renderActivityRow = (activity) => {
        const activityOutputs = outputsByActivity[activity.ID] || [];
        const activityEventFields = eventFieldsByActivity[activity.ID] || [];
        const isExpanded = expandedActivityId === activity.ID;

        return (
            <div key={activity.ID} className="subscribe-modal__item">
                <div className="subscribe-modal__item-header">
                    <p className="subscribe-modal__item-title">
                        {activity.Activity || '(zonder titel)'}{' '}
                        <span className="subscribe-modal__item-meta">
                            ({activityOutputs.length} outputs, {activityEventFields.length} velden)
                        </span>
                    </p>
                    <button
                        onClick={() =>
                            setExpandedActivityId(isExpanded ? '' : activity.ID)
                        }
                    >
                        {isExpanded ? 'Inklappen' : 'Opties'}
                    </button>
                </div>

                {isExpanded && (
                    <div className="subscribe-modal__drilldown">
                        {activityOutputs.length === 0 && activityEventFields.length === 0 && (
                            <p className="subscribe-modal__empty">
                                Geen koppelbare outputs of velden in deze activiteit.
                            </p>
                        )}

                        {activityOutputs.length > 0 && (
                            <div className="subscribe-modal__field">
                                <label className="subscribe-modal__field-label">Outputs</label>
                                <div className="subscribe-modal__checkbox-list">
                                    {activityOutputs.map((output) => (
                                        <label key={output.ID} className="subscribe-modal__checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={selectedOutputIds.has(output.ID)}
                                                onChange={() => toggleOutput(output.ID)}
                                            />
                                            <span>{output.Title || '(zonder titel)'}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activityEventFields.length > 0 && (
                            <div className="subscribe-modal__field">
                                <label className="subscribe-modal__field-label">Velden</label>
                                <div className="subscribe-modal__checkbox-list">
                                    {activityEventFields.map((field) => (
                                        <label key={field.ID} className="subscribe-modal__checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={selectedEventFieldIds.has(field.ID)}
                                                onChange={() => toggleEventField(field.ID)}
                                            />
                                            <span>{field.Title || '(zonder titel)'}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <CommonModal
            modalOpen={modalOpen}
            setModalOpen={(value) => {
                if (!value) closeModal();
                else setModalOpen(true);
            }}
            pageHeader="Activiteiten koppelen"
            description="Kies de outputs en velden die je met dit project wilt delen."
            confirm={handleConfirm}
            confirmDisabled={totalSelected === 0}
            confirmLabel="Koppelingen aanvragen"
            content={
                <div className="subscribe-modal">
                    {activityList.length === 0 ? (
                        <p className="subscribe-modal__empty">Geen activiteiten gevonden.</p>
                    ) : (
                        <div className="subscribe-modal__list">
                            {activityList.map(renderActivityRow)}
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default AddActivitySyncsModal;
